import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Accessory } from '#pplx/frontend/aether/components/Accessory/index.js';
import { Button } from '#pplx/frontend/aether/components/Button/index.js';
import { getInputContainerClasses, getInputFieldClasses, } from '#pplx/frontend/aether/components/Input/classes.js';
import { useMeasureableHandle } from '#pplx/frontend/aether/internal/useMeasureableHandle.js';
import { IconPrimitive } from '#pplx/frontend/aether/primitives/IconPrimitive/index.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import classNames from 'classnames';
export function InputLeadingIconWrapper({ inputRef, children, }) {
    return (_jsx("span", { role: "presentation", className: "inline-flex shrink-0 h-6 items-center justify-center", onClick: () => inputRef.current?.focus(), children: children }));
}
/**
 * Single-line text input with built-in support for leading and trailing accessories, a clear
 * button, error states, and two size variants. Wraps a native HTML input element with Aether
 * styling, focus ring management, and accessibility attributes.
 *
 * @category components
 * @keywords input, components, single-line, text, with, built-in, support, leading, trailing,
 *   accessories, clear, button, error, states, size, variants, wraps, native, html, element,
 *   aether, styling, focus, ring, management, accessibility, attributes, icon, disabled,
 *   select-button, accessory
 * @whenToUse Use Input for single-line text entry in forms, search bars, and inline editing
 *   scenarios. It provides built-in support for leading and trailing accessories, a clear button,
 *   error states, and two size variants. Input builds on the native HTML input element with Aether
 *   styling, focus ring management, and accessibility attributes. For multi-line text, use the
 *   Aether Textarea component. For structured selection from predefined options, use Select or
 *   RadioGroup instead.
 * @guidelineDo
 *   - Always provide an aria-label prop — it is required by the InputProps interface and ensures
 *     the input is accessible to screen readers
 *   - Use the size prop to match surrounding UI density — default (h-10) for standard forms and
 *     small (h-8) for compact toolbars or inline editing
 *   - Use leadingAccessory for contextual icons like search or currency symbols, and
 *     trailingAccessory for actions like visibility toggles or character counts
 *   - Use the onClear prop to provide a built-in clear button — it renders a small X button that is
 *     automatically positioned and styled
 *   - Prefer the onValueChange callback for reacting to text changes — it receives the new string
 *     value directly, without exposing the DOM ChangeEvent
 *   - Set the error prop to true alongside a visible error message to indicate invalid input — this
 *     applies a danger border color and sets aria-invalid on the native input
 * @guidelineDont
 *   - Don't omit the aria-label even when a visible label element is present — the prop is required
 *     by the type system and should describe the input's purpose
 *   - Don't use className to override the container's height or padding — use the size variant
 *     instead, which coordinates sizing across the container, text, and accessories
 *   - Don't place interactive elements inside leadingAccessory — leading accessories are
 *     decorative. Use trailingAccessory or the onClear prop for interactive controls
 *   - Don't combine the icon prop with leadingAccessory — the icon prop renders an IconSprite
 *     before the input and serves the same visual role as a leading accessory
 *   - Don't set disabled without providing context elsewhere in the UI explaining why the input is
 *     unavailable — the disabled state reduces opacity but does not convey a reason
 * @accessibility
 *   - The component requires an aria-label prop, ensuring every Input instance has an accessible
 *     name for screen readers
 *   - When error is true, the native input element receives aria-invalid, which screen readers
 *     announce to inform users the value is invalid
 *   - Clicking anywhere on the container (including the gap between accessories) focuses the inner
 *     input element, enlarging the effective click target
 *   - The container uses the interactable-within class pattern to show a focus ring when the inner
 *     input is focused, providing a clear keyboard focus indicator
 *   - The built-in clear button (rendered via onClear) includes an aria-label of "Clear" and is
 *     keyboard-accessible as a separate tab stop
 * @relatedComponents button, select-button, accessory, icon
 */
export function Input({ size = 'default', error = false, disabled = false, className, leadingAccessory, icon, trailingAccessory, onClear, onChange, onValueChange, type, ref, value, rounded, ...props }) {
    const targetRef = useMeasureableHandle(ref);
    const finalIcon = type === 'search' ? $icon('search') : icon;
    return (_jsxs("div", { className: classNames(getInputContainerClasses({ size, error, disabled, rounded }), className), onClick: (e) => {
            if (e.target === e.currentTarget) {
                targetRef.current?.focus();
            }
        }, children: [finalIcon && (_jsx(InputLeadingIconWrapper, { inputRef: targetRef, children: _jsx(IconPrimitive, { icon: finalIcon, className: "text-secondary size-4" }) })), leadingAccessory && _jsx(Accessory, { accessory: leadingAccessory, color: "quiet" }), _jsx("input", { ref: targetRef, disabled: disabled, "aria-invalid": error || undefined, className: getInputFieldClasses({ size }), type: type, value: value, onChange: (event) => {
                    onValueChange?.(event.target.value);
                    onChange?.(event);
                }, ...props }), onClear && (_jsx("div", { className: classNames('shrink-0 inline-flex', {
                    '-mr-0.5': !trailingAccessory && size === 'default',
                    '-mr-1': !trailingAccessory && size === 'small',
                    'opacity-0 pointer-events-none': !value,
                }), children: _jsx(Button, { icon: $icon('x'), "aria-label": "Clear", onClick: onClear, size: "tiny", rounded: true, variant: "text" }) })), trailingAccessory && _jsx(Accessory, { accessory: trailingAccessory, color: "quieter" })] }));
}
//# sourceMappingURL=index.js.map